import type { OrderStatus } from '@order/graphql';
import {
  checkIfExpiredOrder,
  checkIfLateOrder,
  checkIfPastOrder,
  getEnhancedOrderStatus,
  getOrderTime,
  getOrderTimeRange,
} from '@order/OrderStatus';
import { ignoreTimezone } from '@order/utils';

/**
 * Derives home data from current order status.
 */
export const useHomeOrderStatusData = (props: HomeOrderStatusDataProps) => {
  const { isLoading, orderStatus, hasCart, orderPendingFeedbackId } = props;

  // ──── Query Data ─────────────────────────────────────────────────────

  const orderId = orderStatus?.order?.id;
  const order = orderStatus?.order;
  const flattenedOrderStatus = orderStatus?.flattenedOrderStatus;

  // ──── No Previous Order ──────────────────────────────────────────────

  if (isLoading || !order || !orderId || !flattenedOrderStatus) {
    return {};
  }

  // ──── Time Range ─────────────────────────────────────────────────────

  const orderTimeString = getOrderTime(order);
  const orderTimeDate = orderTimeString
    ? ignoreTimezone(orderTimeString)
    : undefined;
  const orderTimeRange = getOrderTimeRange(orderTimeDate);

  // ──── Timed Statuses ─────────────────────────────────────────────────

  const isLate = checkIfLateOrder({ order, flattenedOrderStatus });
  const isPast = checkIfPastOrder({ order, flattenedOrderStatus });
  const isExpired = checkIfExpiredOrder({ order });

  // ──── Rating In-Store Order ──────────────────────────────────────────

  const shouldUseOrderPendingFeedbackId =
    Boolean(orderPendingFeedbackId) &&
    !checkIfHasHighPriorityStatus(flattenedOrderStatus, hasCart, isPast);
  const isPendingFeedback =
    shouldUseOrderPendingFeedbackId || order?.isPendingFeedback;

  // ──── Derived Statuses ───────────────────────────────────────────────

  const enhancedOrderStatus = getEnhancedOrderStatus({
    orderStatus: flattenedOrderStatus,
    hasCart,
    isLate,
    isPast,
    isPendingFeedback,
    isTrackable: order?.canTrackOrderStatus,
  });

  return {
    order,
    orderId: shouldUseOrderPendingFeedbackId ? orderPendingFeedbackId : orderId,
    isPast,
    isExpired,
    isPendingFeedback,
    enhancedOrderStatus,
    orderTimeDate,
    orderTimeRange,
  };
};

/**
 *  We consider ongoing orders as high priority, these will be shown before rate order requests.
 */
const checkIfHasHighPriorityStatus = (
  flattenedOrderStatus: string,
  hasCart: boolean,
  isPast: boolean,
) => {
  const isOngoingStatus = [
    'received',
    'preparing',
    'readyForCourier',
    'delivering',
  ].includes(flattenedOrderStatus);

  const isRecentlyCompleted = flattenedOrderStatus === 'completed' && !isPast;

  // These flags represent an ongoing order.
  return hasCart || isOngoingStatus || isRecentlyCompleted;
};

// ─── Types ────────────────────────────────────────────────────────

type HomeOrderStatusDataProps = {
  readonly isLoading: boolean;
  readonly hasCart: boolean;
  readonly orderStatus?: OrderStatus | null;
  readonly orderPendingFeedbackId?: string;
};
